import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../axiosConfig';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import './WorkerBundleScanner.css';
import WorkerProductionAction from './WorkerProductionAction';

const base = '/api/wip/worker-scanner'; // Read-only barcode-to-lot discovery.
const productionBase = '/api/wip/production-entries';
const message = error => error.code === 'ECONNABORTED' ? 'Lookup took too long. Please scan again.' : error.response?.data?.message || 'Connection failed. Please retry.';
const newId = () => crypto.randomUUID();

export default function WorkerBundleScanner({ user }) {
  const storageKey = `worker-bundle-draft-v1:${user.EmployeeID}`;
  const [rows, setRows] = useState(() => {
    try { const saved = JSON.parse(sessionStorage.getItem(storageKey) || '[]'); return Array.isArray(saved) ? saved.map(row => ({ ...row, status: row.status === 'Saving' ? 'Retry' : row.status })) : []; }
    catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState('RECEIPT');
  const actionRef = useRef(action);
  actionRef.current = action;
  const [scan, setScan] = useState('');
  const [selectedLot, setSelectedLot] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [supervisor, setSupervisor] = useState(null);
  const [selectSupervisor, setSelectSupervisor] = useState(false);
  const [supervisorLoading, setSupervisorLoading] = useState(false);
  const [supervisorError, setSupervisorError] = useState('');
  async function loadLots(search) {
    try {
      const response = await axios.get(`${productionBase}/lots`, { params: { search, limit: 100 } });
      return (response.data.data || []).map(lot => ({ value: lot, label: lot }));
    } catch (err) { setError(message(err)); return []; }
  }
  async function loadSupervisors() {
    setSupervisorLoading(true); setSupervisorError('');
    try {
      const response = await axios.get(`${productionBase}/supervisors`, { headers: { 'X-Supervisor-Selection': 'v1' } });
      if (!alive.current) return;
      const options = (response.data.data || []).map(s => ({ value: s.employeeId, label: s.name }));
      setSupervisors(options);
      setSelectSupervisor(response.data.supervisorSelectionEnabled === true);
      setSupervisor(current => options.find(s => s.value === current?.value) || (response.data.supervisorSelectionEnabled ? null : options[0]) || null);
    } catch (err) { if (alive.current) setSupervisorError(message(err)); }
    finally { if (alive.current) setSupervisorLoading(false); }
  }
  useEffect(() => { if (open) loadSupervisors(); }, [open]);

  const [error, setError] = useState('');
  const [sheetContext, setSheetContext] = useState(null);
  const [lots, setLots] = useState([]);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lookingUp, setLookingUp] = useState(0);
  const [lastScanned, setLastScanned] = useState('');
  const input = useRef(null);
  const dialog = useRef(null);
  const rowsRef = useRef(rows);
  const alive = useRef(true);
  const pending = useRef(new Set());
  const saving = useRef(false);
  const switching = useRef(false);
  const actionLock = useCallback(value => { saving.current = value; setBusy(value); }, []);
  const scanHandler = useRef(null);
  const scanTimer = useRef(null);
  rowsRef.current = rows;

  const contextLot = sheetContext?.lotId;
  const contextGroup = sheetContext?.processGroup;
  const contextProcess = sheetContext?.processName;
  useEffect(() => {
    if (!contextLot) { setSheetLoading(false); return; }
    let active = true;
    setSheetLoading(true);
    axios.post(`${base}/sheet-context`, { lotId: contextLot, processGroup: contextGroup, processName: contextProcess })
      .then(({ data: response }) => {
        if (!active) return;
        const options = response.data.processOptions.filter(item => item.processName.trim().toUpperCase() === 'FOAM CUTTING');
        const selected = options.find(item => item.processGroup === contextGroup && item.processName === contextProcess) || (options.length === 1 ? options[0] : null);
        setSheetContext(current => current && current.lotId === contextLot ? {
          ...current, options, sheetCounts: response.data.sheetCounts,
          processGroup: selected?.processGroup || '', processName: selected?.processName || '',
        } : current);
      }).catch(err => { if (active) setError(message(err)); })
      .finally(() => { if (active) setSheetLoading(false); });
    return () => { active = false; };
  }, [contextLot, contextGroup, contextProcess]);

  useEffect(() => {
    const guard = event => {
      if (saving.current || pending.current.size) event.preventDefault();
      else switching.current = true;
    };
    const failed = () => { switching.current = false; };
    window.addEventListener('wip-before-worker-switch', guard);
    window.addEventListener('wip-worker-switch-failed', failed);
    return () => {
      window.removeEventListener('wip-before-worker-switch', guard);
      window.removeEventListener('wip-worker-switch-failed', failed);
    };
  }, []);
  useEffect(() => { alive.current = true; return () => { alive.current = false; clearTimeout(scanTimer.current); }; }, []);
  useEffect(() => { if (!open) clearTimeout(scanTimer.current); }, [open]);
  useEffect(() => {
    try { sessionStorage.setItem(storageKey, JSON.stringify(rows)); }
    catch { setError('Draft could not be stored. Keep this page open until entries are saved.'); }
  }, [rows, storageKey]);
  useEffect(() => {
    if (!open) return;
    input.current?.focus();
  }, [open]);

  async function addScan(raw, context = {}) {
    if (actionRef.current !== 'RECEIPT') return;
    clearTimeout(scanTimer.current);
    const barcode = raw.trim().toUpperCase();
    if (/^\d+$/.test(barcode)) { setError('Scan the full bundle barcode, for example BUN5616. A shade number such as 0413 is not a bundle barcode.'); return; }
    if (!/^[A-Z0-9][A-Z0-9._/-]{1,99}$/.test(barcode)) { setError('Scan a bundle, sheet or roll barcode.'); return; }
    setOpen(true);
    setScan('');
    if (switching.current) return;
    if (saving.current) { setError('Saving entries. Scan the next item after saving finishes.'); return; }
    if (selectSupervisor && !supervisor) { setError('Select a supervisor before scanning.'); return; }
    if (selectedLot && !context.lotId) context = { ...context, lotId: selectedLot.value };
    const isSheet = /^SHEET\d+$/i.test(barcode);
    if (isSheet && !context.lotId && sheetContext?.lotId) {
      if (sheetLoading || !sheetContext.processName) {
        setError('Select the sheet lot and FOAM CUTTING process, then scan again.'); return;
      }
      context = { lotId: sheetContext.lotId, processGroup: sheetContext.processGroup, processName: sheetContext.processName };
    }
    const pendingKey = `${barcode}:${context.lotId || ''}`;
    if (pending.current.has(pendingKey)) return;
    if (rowsRef.current.some(row => row.batchNo === barcode &&
      (!isSheet || (context.lotId && row.lotId === context.lotId)))) {
      setError(`${barcode} is already in this list.`); return;
    }
    pending.current.add(pendingKey);
    setLookingUp(count => count + 1);
    setError('');
    try {
      let response;
      if (selectedLot && !isSheet) {
        const [bundleResponse, processResponse] = await Promise.all([
          axios.get(`${productionBase}/bundles`, { params: { lotId: selectedLot.value, includeQuantity: 'true', source: 'reference' }, timeout: 15000 }),
          axios.get(`${productionBase}/process-options`, { params: { lotId: selectedLot.value }, timeout: 15000 }),
        ]);
        const found = bundleResponse.data.data.find(item => item.batchNo.trim().toUpperCase() === barcode);
        if (!found) { setError(`${barcode} is not in selected lot ${selectedLot.value}. Check the sticker and Lot ID.`); return; }
        if (!Number.isInteger(found.quantity) || found.quantity <= 0) { setError(`${barcode}: bundle quantity is missing or conflicting. Ask your supervisor to check the reference data.`); return; }
        response = { data: { batchNo: barcode, lotId: selectedLot.value, quantity: found.quantity,
          workerId: String(user.EmployeeID), processOptions: processResponse.data.data } };
      } else {
        ({ data: response } = await axios.post(`${base}/scan`, { barcodeValue: barcode, ...context }, { timeout: 15000 }));
      }
      if (!alive.current) return;
      const bundle = response.data;
      if (!bundle.needsContext && selectedLot && String(bundle.lotId).trim().toUpperCase() !== selectedLot.value.trim().toUpperCase()) {
        setError(`${barcode} belongs to ${bundle.lotId}, not selected lot ${selectedLot.value}. Select the matching lot.`); return;
      }
      if (bundle.needsContext) {
        setSheetContext({ barcode, ...context, options: bundle.processOptions || [] });
        if (!lots.length) axios.get(`${base}/lots`).then(result => { if (alive.current) setLots(result.data.data); }).catch(err => { if (alive.current) setError(message(err)); });
        return;
      }
      if (isSheet) {
        const option = bundle.processOptions?.find(item => item.processName.trim().toUpperCase() === 'FOAM CUTTING');
        if (!option || Number(bundle.quantity) !== 1) {
          setError('FOAM CUTTING sheets must have quantity 1 and a valid process mapping.'); return;
        }
        bundle.processOptions = [{ ...option, requiresSheetBarcode: false }];
        bundle.quantity = 1;
        setSheetContext(current => ({ ...current, barcode: '', lotId: bundle.lotId,
          ...option, options: [{ ...option, requiresSheetBarcode: false }] }));
      }
      if (String(bundle.workerId) !== String(user.EmployeeID)) throw new Error('Worker changed');
      const matches = bundle.processOptions.filter(option => option.processName.toLowerCase() === String(user.Department || '').trim().toLowerCase());
      const selected = matches.length === 1 ? matches[0] : bundle.processOptions.length === 1 ? bundle.processOptions[0] : null;
      setLastScanned(bundle.batchNo);
      setRows(current => current.some(row => row.batchNo === bundle.batchNo && row.lotId === bundle.lotId) ? current : [...current, {
        ...bundle, supervisorEmployeeId: supervisor?.value, supervisorSelection: selectSupervisor, id: newId(), process: selected ? bundle.processOptions.indexOf(selected) : '',
        rejection: 0, sheetBarcode: '', status: 'Ready', error: '',
      }]);
    } catch (err) { if (alive.current) setError(`${barcode}: ${message(err)}`); }
    finally {
      pending.current.delete(pendingKey);
      if (alive.current) { setLookingUp(count => count - 1); input.current?.focus(); }
    }
  }
  scanHandler.current = addScan;

  function changeScan(value) {
    setScan(value);
    clearTimeout(scanTimer.current);
    // Some HID/data-wedge readers send one input event rather than keydown
    // bursts. Keep manual entry available and lookup after the value settles.
    // Numeric RFID cards remain exclusively handled by RfidQuickLogin.
    const code = value.trim();
    if (!/^(?=.*[A-Z])(?=.*\d)[A-Z0-9][A-Z0-9._/-]{2,99}$/i.test(code)) return;
    scanTimer.current = setTimeout(() => {
      if (alive.current && !saving.current && !switching.current) scanHandler.current(code);
    }, 350);
  }

  useEffect(() => {
    let buffer = '';
    let last = 0;
    let timer;
    let origin = null;
    let originalValue = '';
    const isBarcode = value => !/^\d{1,10}$/.test(value) && /^(?=.*\d)[A-Z0-9][A-Z0-9._/-]{2,99}$/i.test(value) &&
      (origin === input.current || /[A-Z]/i.test(value) || value.length >= 8);
    const restoreField = () => {
      if (!origin || origin === input.current) return;
      if ('value' in origin) {
        const prototype = Object.getPrototypeOf(origin);
        const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
        setter?.call(origin, originalValue);
        origin.dispatchEvent(new Event('input', { bubbles: true }));
        origin.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (origin.isContentEditable) origin.textContent = originalValue;
    };
    const finish = () => {
      clearTimeout(timer);
      const barcode = buffer;
      buffer = '';
      if (!isBarcode(barcode) || /^\d{10}$/.test(barcode)) return;
      restoreField();
      input.current?.focus();
      scanHandler.current(barcode);
    };
    const onKey = event => {
      if (actionRef.current !== 'RECEIPT') { clearTimeout(timer); buffer = ''; return; }
      if (typeof event.key !== 'string' || !event.key) {
        clearTimeout(timer); buffer = ''; return;
      }
      if (event.key === 'Shift') return;
      if (event.ctrlKey || event.altKey || event.metaKey || event.isComposing) {
        clearTimeout(timer); buffer = ''; return;
      }
      const target = event.target;
      if (target?.closest?.('.worker-context-select')) { clearTimeout(timer); buffer = ''; return; }
      // These dedicated fields scan a matching sheet label for an existing row.
      if (target?.getAttribute?.('aria-label')?.startsWith('Sheet barcode ')) {
        clearTimeout(timer); buffer = ''; return;
      }
      clearTimeout(timer);
      // Capture scanner bursts even inside searchable selects and other fields.
      // Ordinary typing is left alone; restore the field only for a full barcode.
      if (Date.now() - last > 120) buffer = '';
      last = Date.now();
      if (event.key === 'Enter' || event.key === 'Tab') {
        if (isBarcode(buffer)) {
          event.preventDefault();
          event.stopPropagation();
          finish();
        } else buffer = '';
      } else if (event.key.length === 1) {
        if (!buffer) {
          origin = target;
          originalValue = target?.value ?? target?.textContent ?? '';
        }
        buffer = (buffer + event.key).slice(-100);
        if (isBarcode(buffer)) {
          event.preventDefault();
          event.stopPropagation();
          timer = setTimeout(finish, 150);
        }
      } else buffer = '';
    };
    window.addEventListener('keydown', onKey, true);
    return () => { clearTimeout(timer); window.removeEventListener('keydown', onKey, true); };
  }, []);

  const update = (id, patch) => setRows(current => current.map(row => row.id === id ? { ...row, ...patch } : row));
  async function checkSheet(row) {
    const option = row.processOptions[row.process];
    if (!option || !row.sheetBarcode.trim()) return;
    const value = row.sheetBarcode;
    update(row.id, { sheetCheck: 'Checking…' });
    try {
      await axios.post(`${base}/validate-sheet`, { barcodeValue: row.batchNo, lotId: row.lotId,
        ...option, sheetBarcodeValue: value });
      if (alive.current) setRows(current => current.map(item => item.id === row.id && item.sheetBarcode === value && item.process === row.process
        ? { ...item, sheetCheck: 'Sheet matched' } : item));
    } catch (err) {
      if (alive.current) setRows(current => current.map(item => item.id === row.id && item.sheetBarcode === value && item.process === row.process
        ? { ...item, sheetCheck: message(err) } : item));
    }
  }
  async function saveEntries() {
    if (switching.current || saving.current || pending.current.size) return;
    if (scan.trim()) {
      await addScan(scan);
      return; // Finish the current scanner input before submitting reviewed rows.
    }
    const unsaved = rowsRef.current.filter(row => row.status !== 'Saved');
    if (!unsaved.length) return;
    if (unsaved.some(row => !row.payload && (row.process === '' ||
      !Number.isInteger(Number(row.rejection)) || Number(row.rejection) < 0 || Number(row.rejection) > row.quantity ||
      (row.processOptions[row.process]?.requiresSheetBarcode && !row.sheetBarcode.trim())))) {
      setError('Select a process; check rejection quantities and required sheet barcodes.'); return;
    }
    saving.current = true;
    setBusy(true);
    setError('');
    try {
      for (const row of unsaved) {
        if (!alive.current) break;
        const option = row.processOptions[row.process];
        const payload = row.payload || { barcodeValue: row.batchNo, lotId: row.lotId,
          processGroup: option.processGroup, processName: option.processName, actionType: 'RECEIPT',
          expectedQuantity: row.quantity, rejectionQuantity: Number(row.rejection),
          sheetBarcodeValue: row.sheetBarcode.trim(), labelQuantity: row.quantity,
          ...(row.supervisorSelection ? { supervisorEmployeeId: row.supervisorEmployeeId } : {}) };
        // Freeze retries to the same payload and id, including after a lost response/reload.
        update(row.id, { status: 'Saving', payload, error: '' });
        try {
          // Match the old web endpoint's internal key namespace, including restored
          // drafts. The mobile endpoint derives the same database ID from it.
          const response = await axios.post(productionBase, {
            ...payload, labelQuantity: payload.labelQuantity ?? payload.expectedQuantity,
          }, {
            headers: { 'Idempotency-Key': `web-scanner:${row.id}`, 'X-Offline-Worker': String(user.EmployeeID),
              ...(row.supervisorSelection ? { 'X-Supervisor-Selection': 'v1' } : {}) },
          });
          if (alive.current) update(row.id, { status: 'Saved', entryId: response.data.data.id });
        } catch (err) {
          if (alive.current) update(row.id, { status: 'Retry', error: message(err),
            // A definitive validation rejection can be removed and scanned again.
            // Connection failures keep the original request intact for a safe retry.
            canReview: [400, 409, 422].includes(err.response?.status) });
          if ([401, 403].includes(err.response?.status)) break;
        }
      }
    } finally {
      saving.current = false;
      if (alive.current) { setBusy(false); input.current?.focus(); }
    }
  }

  function modalKey(event) {
    if (event.key === 'Escape' && !busy) setOpen(false);
    if (event.key !== 'Tab') return;
    const controls = [...dialog.current.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled)')];
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }
  const unsavedCount = rows.filter(row => row.status !== 'Saved').length;
  return <>
    <div className="bundle-launch">
      <div><strong>Production scanner</strong><span>USB / Bluetooth barcode machine: scan items, sheets or rolls — your list opens automatically.</span></div>
      <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>Scan items{unsavedCount ? ` (${unsavedCount})` : ''}</button>
    </div>
    {open && <div className="bundle-overlay">
      <section className="bundle-dialog" role="dialog" aria-modal="true" aria-labelledby="bundle-title" ref={dialog} onKeyDown={modalKey}>
        <header><div><h2 id="bundle-title">Scan items</h2><p>{user.Name} · {user.EmployeeID} · {user.Department}</p></div>
          <button type="button" className="btn btn-light" aria-label="Close production scanner" disabled={busy} onClick={() => setOpen(false)}>✕</button></header>
        <div className="bundle-body">
          <div className="worker-context-select">
            <div className="worker-context-field"><label htmlFor="scanner-lot">Lot ID</label>
              <AsyncSelect className="scanner-select" classNamePrefix="scanner-select" inputId="scanner-lot" aria-label="Lot ID" defaultOptions loadOptions={loadLots} value={selectedLot}
                placeholder="Search and select Lot ID" isClearable isDisabled={busy || !!lookingUp}
                onChange={value => { setSelectedLot(value); setSheetContext(null); setError(''); }} /></div>
            <div className="worker-context-field"><label htmlFor="scanner-supervisor">Supervisor</label>
              <Select className="scanner-select" classNamePrefix="scanner-select" inputId="scanner-supervisor" aria-label="Supervisor" options={supervisors} value={supervisor}
                placeholder="Search and select supervisor" isLoading={supervisorLoading}
                isDisabled={busy || !!lookingUp || supervisorLoading || !selectSupervisor}
                onChange={setSupervisor} />
              {supervisorError && <div role="alert">{supervisorError}<button type="button" onClick={loadSupervisors}>Retry supervisors</button></div>}
            </div>
          </div>
          <div className="worker-context-select">
            <div className="worker-context-field"><label htmlFor="worker-action">Action Type</label>
              <select id="worker-action" value={action} disabled={busy || !!lookingUp || (action === 'RECEIPT' && unsavedCount > 0)} onChange={event => {clearTimeout(scanTimer.current);setScan('');setError('');setAction(event.target.value);}}>
                <option value="ISSUE">ISSUE</option><option value="RECEIPT">RECEIPT</option><option value="BUNDLE_RECO">BUNDLE RECO</option>
              </select>
              {action === 'RECEIPT' && unsavedCount > 0 && <small>Save or clear the receipt list before changing Action Type.</small>}
            </div>
          </div>
          {action !== 'RECEIPT' && <WorkerProductionAction key={action} action={action} lotId={selectedLot?.value} supervisorId={supervisor?.value} selectionEnabled={selectSupervisor} user={user} onLock={actionLock} />}
          {action === 'RECEIPT' && <>
          <form className="bundle-scan-form" onSubmit={event => { event.preventDefault(); addScan(scan); }}>
            <label htmlFor="bundle-barcode">Barcode<input id="bundle-barcode" ref={input} value={scan} autoComplete="off" placeholder="Ready for your scanner…" disabled={busy}
              onChange={event => changeScan(event.target.value)} /></label>
            <button className="btn btn-primary" disabled={busy || !scan.trim()}>Add scan</button>
          </form>
          {sheetContext && <div className="scan-context">
            <strong>{sheetContext.barcode || 'Continuous sheet scanning'} · Sheet details</strong>
            <p>This sheet number repeats across lots. Select its lot and FOAM CUTTING process once, then keep scanning. Each sheet counts as quantity 1. Change the lot before scanning another lot.</p>
            <div className="scan-context-fields">
              <label>Sheet lot<select disabled={busy || !!lookingUp} value={sheetContext.lotId || ''} onChange={event => setSheetContext({ ...sheetContext, lotId: event.target.value, options: [], sheetCounts: null, processGroup: '', processName: '' })}>
                <option value="">Select lot</option>{lots.map(lot => <option key={lot}>{lot}</option>)}</select></label>
              {sheetContext.options.length > 0 && <label>Sheet process<select disabled={busy || !!lookingUp} value={sheetContext.options.findIndex(item => item.processName === sheetContext.processName && item.processGroup === sheetContext.processGroup)} onChange={event => {
                const option = sheetContext.options[Number(event.target.value)];
                setSheetContext({ ...sheetContext, ...option });
              }}><option value="-1">Select process</option>{sheetContext.options.map((option, index) => <option key={`${option.processGroup}:${option.processName}`} value={index}>{option.processName} · {option.processGroup}</option>)}</select></label>}
              <button type="button" className="btn btn-primary" disabled={busy || sheetLoading || !!lookingUp || !sheetContext.barcode || !sheetContext.lotId || !sheetContext.processName} onClick={() => addScan(sheetContext.barcode, { lotId: sheetContext.lotId, processGroup: sheetContext.processGroup, processName: sheetContext.processName })}>Add sheet</button>
              <button type="button" className="btn btn-light" disabled={busy || !!lookingUp} onClick={() => setSheetContext(null)}>Clear sheet selection</button>
            </div>
            {sheetLoading && <p role="status">Loading sheet mapping…</p>}
            {!sheetLoading && sheetContext.lotId && !sheetContext.options.length && <p role="alert">No FOAM CUTTING mapping found for this lot.</p>}
            {!sheetLoading && sheetContext.sheetCounts && <p className="sheet-count-info">Upper: {sheetContext.sheetCounts.upper} · Base: {sheetContext.sheetCounts.base} · Valid sheet numbers: 1–{sheetContext.sheetCounts.total}. Each scan adds 1 sheet.</p>}
          </div>}
          <div aria-live="polite">
            <p>Keep scanning to add multiple barcodes. Check each rejection quantity, then Save entries once.</p>
            {lastScanned && <p role="status">Last scanned: {lastScanned}</p>}
            {lookingUp > 0 && <p>Looking up {lookingUp} item(s)…</p>}{error && <p className="bundle-error" role="alert">{error}</p>}
          </div>
          <div className="bundle-table-wrap"><table className="bundle-table"><thead><tr><th>Barcode / Lot</th><th>Process</th><th>Qty</th><th>Rejection</th><th>Status</th><th /></tr></thead>
            <tbody>{rows.map(row => <tr key={row.id}>
              <td><span className="scan-kind">{row.scanType || (/^BUN/i.test(row.batchNo) ? 'Bundle' : 'Barcode')}</span><strong>{row.batchNo}</strong><small>{row.lotId}</small></td>
              <td><select aria-label={`Process ${row.batchNo}`} disabled={busy || !!row.payload} value={row.process} onChange={event => update(row.id, { process: event.target.value, sheetCheck: '' })}>
                <option value="">Select process</option>{row.processOptions.map((option, index) => <option key={index} value={index}>{option.processName} · {option.processGroup}</option>)}</select>
                {row.processOptions[row.process]?.requiresSheetBarcode && <div className="sheet-match-field"><small>Scan matching sheet lot: {row.lotId}</small><input aria-label={`Sheet barcode ${row.batchNo}`} placeholder="Scan sheet lot barcode" value={row.sheetBarcode} disabled={busy || !!row.payload} onChange={event => update(row.id, { sheetBarcode: event.target.value, sheetCheck: '' })} onBlur={() => checkSheet(row)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); input.current?.focus(); } }} /><small aria-live="polite">{row.sheetCheck}</small></div>}</td>
              <td>{row.quantity}</td><td><input aria-label={`Rejection ${row.batchNo}`} type="number" min="0" max={row.quantity} step="1" value={row.rejection} disabled={busy || !!row.payload} onChange={event => update(row.id, { rejection: event.target.value })} /></td>
              <td><span className={row.status === 'Saved' ? 'bundle-saved' : ''}>{row.status}</span>{row.error && <small className="bundle-error">{row.error}</small>}</td>
              <td>{(!row.payload || row.canReview) && row.status !== 'Saved' && <button className="btn btn-sm btn-outline-danger" disabled={busy} aria-label={`Remove ${row.batchNo}`} onClick={() => setRows(current => current.filter(item => item.id !== row.id))}>Remove</button>}</td>
            </tr>)}{!rows.length && <tr><td colSpan="6" className="bundle-empty">Ready to scan. Your items will appear here.</td></tr>}</tbody></table></div>
          </>}
        </div>
        {action === 'RECEIPT' && <footer><span>{rows.length} items · {rows.reduce((sum, row) => sum + row.quantity, 0)} qty · {rows.filter(row => row.status === 'Saved').length} saved</span>
          <button type="button" className="btn btn-light" disabled={busy || !!lookingUp || (!rows.length && !sheetContext && !scan)} onClick={() => {
            if (switching.current || saving.current || pending.current.size) return;
            setRows([]);
            rowsRef.current = [];
            setSheetContext(null);
            setScan('');
            clearTimeout(scanTimer.current);
            setLastScanned('');
            setError('');
            input.current?.focus();
          }}>Clear all</button>
          <button className="btn btn-light" disabled={busy || !rows.some(row => row.status === 'Saved')} onClick={() => setRows(current => current.filter(row => row.status !== 'Saved'))}>Clear saved</button>
          <button className="btn btn-primary" disabled={busy || !!lookingUp || !unsavedCount} onClick={saveEntries}>{busy ? 'Saving…' : `Save entries (${unsavedCount})`}</button></footer>}
      </section>
    </div>}
  </>;
}
